import type { ButtonHTMLAttributes, ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Buttons.
 *
 * Four intents, two sizes. `touch` is 44px tall and is what handover and
 * return screens use — those are operated one-handed, outdoors, in a hurry.
 *
 * Flat colour, 3px radius, one border. No gradient, no shadow, no ring stack.
 */

type Intent = 'primary' | 'secondary' | 'quiet' | 'danger';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly intent?: Intent;
  readonly size?: 'base' | 'touch';
  readonly children: ReactNode;
}

const SHARED =
  'inline-flex items-center justify-center gap-2 rounded border font-medium transition-colors';

const INTENT_CLASSES: Readonly<Record<Intent, string>> = {
  primary: 'bg-signal text-ink-inverse border-signal hover:bg-signal/90',
  secondary: 'bg-panel text-ink border-rule-strong hover:bg-paper',
  quiet: 'bg-transparent text-ink-muted border-transparent hover:bg-paper hover:text-ink',
  danger: 'bg-panel text-alert border-alert/40 hover:bg-alert-wash',
};

export function Button({
  intent = 'secondary',
  size = 'base',
  type = 'button',
  children,
  ...rest
}: ButtonProps): ReactElement {
  return (
    <button
      type={type}
      className={[
        SHARED,
        'disabled:cursor-not-allowed disabled:opacity-45',
        sizeClasses(size),
        INTENT_CLASSES[intent],
      ].join(' ')}
      {...rest}
    >
      {children}
    </button>
  );
}

function sizeClasses(size: 'base' | 'touch'): string {
  return size === 'touch' ? 'min-h-touch px-4 text-body' : 'h-8 px-3 text-base';
}

interface LinkButtonProps {
  readonly to: string;
  readonly intent?: Intent;
  readonly size?: 'base' | 'touch';
  readonly children: ReactNode;
}

/**
 * A control that navigates.
 *
 * It looks like a button and behaves like a link, which is what it is: it has a
 * URL, it opens in a new tab on a middle click, and it is reachable by keyboard
 * without any help. A <button> wrapped around a link is neither.
 */
export function LinkButton({
  to,
  intent = 'secondary',
  size = 'base',
  children,
}: LinkButtonProps): ReactElement {
  return (
    <Link to={to} className={[SHARED, sizeClasses(size), INTENT_CLASSES[intent]].join(' ')}>
      {children}
    </Link>
  );
}
